
import MBaseModal from "@/components/MBaseModal.vue";
import MButton from "@/components/MButton.vue";
import MSelectBox from "@/components/form/MSelectBox.vue";
import MTextField from "@/components/form/MTextField.vue";
import MIconFileInput from "@/components/form/MIconFileInput.vue";
import MsStudentPicker from "@/components/student/MsStudentPicker.vue";
import MIcon from "@/components/MIcon.vue";
import MTextArea from "@/components/form/MTextArea.vue";
import store from "@/store";
import { Options, Vue } from "vue-class-component";
import { Student } from "@/entities/student";
import {
  JournalData,
  JournalLabel,
  JournalReflectionItem,
  JournalReflectionType,
  TutorAccessType
} from "@/entities/journal";
import { v4 as uuid } from "uuid";
import dayjs from "dayjs";
import { State } from "@/store/helper";
import { JournalForTable } from "@/views/JournalList.vue";
import { updateJournal } from "@/api/journal";

@Options({
  components: {
    MBaseModal,
    MButton,
    MSelectBox,
    MIconFileInput,
    MTextField,
    MsStudentPicker,
    MIcon,
    MTextArea
  },
  emits: ["close", "syncUpdate"],
  props: {
    journal: Object,
    students: Array
  }
})
export default class JournalEditModal extends Vue {
  @State("allStudentsFetched") allStudentsFetched?: boolean;

  journal!: JournalForTable;
  students: Student[] = [];

  title = "";
  titleEntered = false;
  reflectionTypes: { value: JournalReflectionType; label: string }[] = [
    { value: "text", label: "テキスト" },
    { value: "graph", label: "数値" }
  ];
  reflectionItems: JournalReflectionItem[] = [];
  availableToAllStudents = false;
  selectedStudents: Student[] = [];
  description = "";
  label: JournalLabel = {
    name: "",
    description: ""
  };
  tutorAccess: TutorAccessType = "publicToOwner";
  prompt = "";
  promptEntered = false;
  includeChatInPrompt = false;

  get selectedStudentIds(): string[] {
    return this.selectedStudents.map(s => s.ref.id);
  }

  //タイトル
  get validTitle(): boolean {
    return this.title.length > 0;
  }

  //振り返り項目
  get validReflectionItems(): boolean {
    if (this.reflectionItems.length < 1) return false;

    for (const item of this.reflectionItems) {
      if (!this.validReflectionItem(item)) {
        return false;
      }
    }

    return true;
  }
  validReflectionItem(item: JournalReflectionItem): boolean {
    return !!item.title && !!item.type && !!item.uid;
  }

  //description
  get validDescription(): boolean {
    return true;
  }

  //label
  get validLabel(): boolean {
    return true;
  }

  //tutorAccess
  get validTutorAccess() {
    return ["private", "public", "publicToOwner"].includes(this.tutorAccess);
  }

  //prompt
  get validPrompt(): boolean {
    return this.prompt.length > 0;
  }

  //total validation
  get validData(): boolean {
    return (
      this.validTitle &&
      this.validReflectionItems &&
      this.validDescription &&
      this.validLabel &&
      this.validTutorAccess &&
      this.validPrompt
    );
  }

  //utils
  isReflectionItemTypeDisabled(item: JournalReflectionItem): boolean {
    return item.createdAt > 0;
  }

  updateSelectedStudents(students: Student[]) {
    if (students.length === 0) {
      this.selectedStudents = [];
      return;
    }

    this.selectedStudents = [...students];
  }

  updateReflectionItemTitle(val: string, index: number) {
    this.reflectionItems[index].title = val;
  }

  updateReflectionItemType(val: JournalReflectionType, index: number) {
    this.reflectionItems[index].type = val;
  }

  generateDefaultReflectionItem(): JournalReflectionItem {
    return {
      title: "",
      type: "text",
      createdAt: 0,
      uid: uuid()
    };
  }

  addEmptyReflectionItem() {
    this.reflectionItems.push(this.generateDefaultReflectionItem());
  }

  removeReflectionItem(index: number) {
    if (this.reflectionItems.length <= 1) return;
    this.reflectionItems.splice(index, 1);
  }

  //emit
  close() {
    this.$emit("close");
  }

  syncUpdate(updatedJournal: JournalForTable) {
    this.$emit("syncUpdate", updatedJournal);
  }

  async markJournalAsCompleted() {
    if (!store.state.tutor) return;

    if (!window.confirm("このジャーナルのステータスを完了にしますか？")) {
      return;
    }

    try {
      const updatedData = {
        isCompleted: true,
        updatedAt: [...(this.journal.updatedAt ?? []), dayjs().unix()],
        updatedBy: [
          ...(this.journal.updatedBy ?? []),
          store.state.tutor.main.ref.id
        ]
      };

      await updateJournal(this.journal.ref, updatedData);

      this.syncUpdate({
        ...this.journal,
        ...updatedData,
        status: "完了"
      });

      alert("ステータスを完了に更新しました。");
    } catch (e) {
      console.error(e);
      alert("問題が発生しました。");
    } finally {
      this.$emit("close");
    }
  }

  async markJournalAsInProgress() {
    if (!store.state.tutor) return;

    if (!window.confirm("このジャーナルの完了ステータスを解除しますか？")) {
      return;
    }

    try {
      const updatedData = {
        isCompleted: false,
        updatedAt: [...(this.journal.updatedAt ?? []), dayjs().unix()],
        updatedBy: [
          ...(this.journal.updatedBy ?? []),
          store.state.tutor.main.ref.id
        ]
      };

      await updateJournal(this.journal.ref, updatedData);

      this.syncUpdate({
        ...this.journal,
        ...updatedData,
        status: "未完了"
      });

      alert("ステータスの完了に更新しました。");
    } catch (e) {
      console.error(e);
      alert("問題が発生しました。");
    } finally {
      this.$emit("close");
    }
  }

  async publishJournal() {
    if (!store.state.tutor) return;

    if (!window.confirm("このジャーナルを公開しますか？")) {
      return;
    }

    try {
      const updatedData: Partial<JournalData> = {
        isDraft: false,
        publishedAt: dayjs().unix(),
        publishedBy: store.state.tutor.main.ref.id
      };

      await updateJournal(this.journal.ref, updatedData);

      this.syncUpdate({
        ...this.journal,
        ...updatedData,
        draftStatus: "公開中",
        publishedTimeStamp: dayjs
          .unix(updatedData.publishedAt!)
          .format("YYYY-MM-DD")
      });

      alert("ジャーナルを公開しました。");
    } catch (e) {
      console.error(e);
      alert("問題が発生しました。");
    } finally {
      this.$emit("close");
    }
  }

  //更新
  async updateJournal() {
    if (!this.validData) return;
    if (!store.state.school) return;
    if (!store.state.tutor) return;

    if (!this.reflectionItems.some(item => item.type === "text")) {
      alert("テキストタイプは必ず1個以上登録してください。");
      return;
    }

    try {
      store.commit("START_LOADING", "ジャーナル更新中...");

      const updatedData: JournalData = {
        title: this.title,
        description: this.description,
        label: this.label,
        tutorAccess: this.tutorAccess,
        journalReflectionItems: this.reflectionItems.map(item => ({
          ...item,
          createdAt: item.createdAt || dayjs().unix()
        })),
        availableToAllStudents: this.availableToAllStudents,
        studentIds: this.availableToAllStudents
          ? []
          : this.selectedStudents.map(s => s.ref.id),
        isCompleted: this.journal.isCompleted,
        isDeleted: this.journal.isDeleted,
        createdAt: this.journal.createdAt,
        createdBy: this.journal.createdBy,
        updatedAt: [...(this.journal.updatedAt ?? []), dayjs().unix()],
        updatedBy: [
          ...(this.journal.updatedBy ?? []),
          store.state.tutor.main.ref.id
        ],
        isDraft: this.journal.isDraft,
        publishedAt: this.journal.publishedAt,
        publishedBy: this.journal.publishedBy,
        prompt: this.prompt,
        includeChatDataInPrompt: this.includeChatInPrompt
      };

      await updateJournal(this.journal.ref, updatedData);

      const updatedJournal: JournalForTable = {
        ...updatedData,
        id: this.journal.id,
        ref: this.journal.ref,
        createdTimeStamp: this.journal.createdTimeStamp,
        publishedTimeStamp: this.journal.publishedTimeStamp,
        targetStudents: this.availableToAllStudents
          ? "全員"
          : this.selectedStudents.map(s => s.data.name).join(", "),
        status: this.journal.status,
        draftStatus: this.journal.draftStatus
      };

      this.syncUpdate(updatedJournal);
      alert("ジャーナルを更新しました。");
      this.close();
    } catch (e) {
      console.error(e);
      alert(
        "ジャーナルの更新に失敗しました。" +
          "\n" +
          (e instanceof Error ? e.message : "")
      );
    } finally {
      store.commit("END_LOADING");
    }
  }

  created(): void {
    this.title = this.journal.title;
    this.reflectionItems = [...this.journal.journalReflectionItems];
    this.availableToAllStudents = this.journal.availableToAllStudents;
    this.description = this.journal.description || "";
    this.label = this.journal.label || { name: "", description: "" };
    this.tutorAccess = this.journal.tutorAccess || "publicToOwner";
    this.prompt = this.journal.prompt || "";
    this.includeChatInPrompt = this.journal.includeChatDataInPrompt ?? false;

    if (!this.availableToAllStudents) {
      this.selectedStudents = this.students.filter(student =>
        this.journal.studentIds.some(id => id === student.ref.id)
      );
    }
  }
}
